import { debounce } from '@dcxwam/dcx-wam-ui-foundations'

type ProductListObserverProps = {
  container: HTMLElement
  selector: string
  onLoaded?(entries: IntersectionObserverEntry[]): void
  onScroll?(entries: IntersectionObserverEntry[]): void
  onInterval?(): void
  onBeforeUnload?(e?: BeforeUnloadEvent): void
}

type ProductListObserverInit = {
  timeout?: number
  wait?: number
} & IntersectionObserverInit

const TIMEOUT = 15e3
const WAIT = 1e2
const INTERSECTION_OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0.1
}

class ProductListObserver {
  private readonly timer!: number

  private readonly options!: IntersectionObserverInit

  constructor(
    {
      container,
      selector,
      onLoaded: onLoadedProp,
      onInterval: onIntervalProp,
      onScroll: onScrollProp,
      onBeforeUnload: onBeforeUnloadProp
    }: ProductListObserverProps,
    options: ProductListObserverInit = {}
  ) {
    const { timeout = TIMEOUT, wait = WAIT, ...restOptions } = options

    this.options = {
      ...INTERSECTION_OBSERVER_OPTIONS,
      ...restOptions
    }

    if (onLoadedProp) {
      this.observerHandler(container, selector, onLoadedProp)
    }

    if (onIntervalProp) {
      this.timer = window.setInterval(onIntervalProp, timeout)
    }

    if (onScrollProp) {
      window.addEventListener(
        'scroll',
        debounce(() => {
          this.observerHandler(container, selector, onScrollProp)
        }, wait)
      )
    }

    if (onBeforeUnloadProp) {
      window.addEventListener('beforeunload', onBeforeUnloadProp)
    }

    this.disconnect = () => {
      if (onIntervalProp) {
        window.clearInterval(this.timer)
      }

      if (onScrollProp) {
        window.removeEventListener(
          'scroll',
          debounce(() => {
            this.observerHandler(container, selector, onScrollProp)
          }, wait)
        )
      }

      if (onBeforeUnloadProp) {
        window.removeEventListener('beforeunload', onBeforeUnloadProp)
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  disconnect() {
    // Implementation is provided in the constructor
    // This method exists to satisfy the interface requirements
  }

  // eslint-disable-next-line class-methods-use-this
  private observerHandler(
    container: HTMLElement,
    selector: string,
    callback?: (entries: IntersectionObserverEntry[]) => void
  ) {
    const elements = container.querySelectorAll(selector)

    const observer = new IntersectionObserver((entries) => {
      callback?.(entries)
      entries.forEach(({ target }) => observer?.unobserve(target))
      observer.disconnect()
    }, this.options)

    elements?.forEach((target) => observer.observe(target))
  }
}

export default ProductListObserver
